/**
 * READ A QR CODE, ON THIS DEVICE (resident-portal Plan 6, RP-6.4).
 *
 * The desk's "Scan a pass" reads the QR on a guest's phone. Nothing about it
 * leaves the tablet: no frame is uploaded, no model is asked, nothing is paid
 * for. Two decoders, in order:
 *
 *   1. BarcodeDetector — the browser's own, where it exists (Chrome on Android
 *      and ChromeOS, Safari 17+ on macOS). Fast, native.
 *   2. jsQR — pure JavaScript (Apache-2.0), for everywhere else, including the
 *      iPad the desk actually uses. Loaded on first use only, so no other
 *      screen pays for it.
 *
 * Both are given a frame DOWNSCALED to at most 800px on its long edge: a QR on
 * a phone held up to the camera fills a good share of the picture, and a
 * full-resolution frame only makes jsQR slower.
 */

const MAX_EDGE = 800;

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

type JsQr = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  options?: { inversionAttempts?: 'dontInvert' | 'onlyInvert' | 'attemptBoth' | 'invertFirst' }
) => { data: string } | null;

let native: BarcodeDetectorLike | null | undefined;
let jsqr: JsQr | null = null;

/** The browser's detector, if it can read QR codes here. Asked once. */
const nativeDetector = async (): Promise<BarcodeDetectorLike | null> => {
  if (native !== undefined) return native;
  native = null;
  const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return native;
  try {
    const formats = Ctor.getSupportedFormats ? await Ctor.getSupportedFormats() : ['qr_code'];
    if (formats.includes('qr_code')) native = new Ctor({ formats: ['qr_code'] });
  } catch {
    native = null;
  }

  return native;
};

const loadJsQr = async (): Promise<JsQr> => {
  if (jsqr) return jsqr;
  const mod = (await import('jsqr')) as unknown as { default: JsQr };
  jsqr = mod.default;

  return jsqr;
};

/** The frame as pixels, at most MAX_EDGE on its long side. */
const pixelsOf = (
  source: CanvasImageSource,
  width: number,
  height: number
): { canvas: HTMLCanvasElement; data: ImageData } | null => {
  if (!width || !height) return null;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings);
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  return { canvas, data: ctx.getImageData(0, 0, canvas.width, canvas.height) };
};

/**
 * The text of the first QR code in the picture, or null. Never throws: a frame
 * with no code in it is the normal answer while the guest is still aiming.
 */
export const decodeQr = async (
  source: CanvasImageSource,
  width: number,
  height: number
): Promise<string | null> => {
  try {
    const frame = pixelsOf(source, width, height);
    if (!frame) return null;

    const detector = await nativeDetector();
    if (detector) {
      try {
        const found = await detector.detect(frame.canvas);
        const hit = found.find(code => typeof code.rawValue === 'string' && code.rawValue);
        if (hit) return hit.rawValue;
      } catch {
        // fall through to jsQR
      }
    }

    const read = await loadJsQr();
    const result = read(frame.data.data, frame.data.width, frame.data.height, {
      inversionAttempts: 'attemptBoth',
    });

    return result && result.data ? result.data : null;
  } catch {
    return null;
  }
};

/** A photo file (the OS-camera fallback) -> the QR's text, or null. */
export const decodeQrFromFile = async (file: File): Promise<string | null> => {
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file);
      const out = await decodeQr(bitmap, bitmap.width, bitmap.height);
      bitmap.close?.();

      return out;
    }
    const url = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode failed'));
        el.src = url;
      });

      return await decodeQr(image, image.naturalWidth, image.naturalHeight);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return null;
  }
};

/** Tests only: forget the detectors chosen so far. */
export const resetQrDecoders = (): void => {
  native = undefined;
  jsqr = null;
};
